import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaCogs, FaLightbulb, FaGraduationCap, FaRocket, FaGlobe, FaEnvelope } from 'react-icons/fa';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: var(--space-3xl);
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: var(--space-lg);
  color: var(--secondary-900);
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: var(--secondary-600);
  max-width: 600px;
  margin: 0 auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-2xl);
  align-items: stretch;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-2xl);
  padding-bottom: var(--space-3xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--secondary-200);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 100%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-500), var(--primary-600));
    transform: scaleX(1);
    transform-origin: left;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--primary-300);

    &::before {
      transform: scaleX(1.1);
    }
  }

  h3 {
    color: var(--primary-600);
    margin-bottom: var(--space-md);
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    min-height: 2.5rem;
    transition: color 0.3s ease;
  }

  .icon {
    font-size: 1.5rem;
    color: var(--primary-500);
    flex-shrink: 0;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover .icon {
    transform: scale(1.2) rotate(10deg);
  }

  .description {
    margin-bottom: var(--space-lg);
    line-height: 1.6;
    color: var(--secondary-700);
    min-height: 3rem;
    transition: color 0.3s ease;
  }

  .features {
    list-style: none;
    padding: 0;
    margin-bottom: var(--space-lg);
    flex: 1 1 auto;
    min-height: 0;
    overflow: visible;

    li {
      padding: var(--space-xs) 0;
      border-bottom: 1px solid var(--secondary-100);
      display: flex;
      align-items: flex-start;
      transition: transform 0.2s ease;
      word-wrap: break-word;
      overflow-wrap: break-word;
      
      &:before {
        content: '✓';
        color: var(--success-600);
        font-weight: bold;
        margin-right: var(--space-sm);
        flex-shrink: 0;
        transition: transform 0.2s ease;
      }

      &:hover {
        transform: translateX(4px);
        
        &:before {
          transform: scale(1.2);
        }
      }
    }
  }

  .price-section {
    margin-top: auto;
    padding-top: var(--space-md);
    padding-bottom: var(--space-xs);
    border-top: 1px solid var(--secondary-100);
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }

  &:hover .price-section {
    transform: translateY(-2px);
  }

  .price {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--primary-600);
    margin-bottom: var(--space-xs);
    transition: color 0.3s ease;
  }

  .vat-note {
    font-size: 0.75rem;
    color: var(--secondary-400);
    font-style: italic;
    margin-bottom: var(--space-xs);
  }

  .duration {
    font-size: 0.875rem;
    color: var(--secondary-500);
  }
`;

const ContactButton = styled(Link)`
  margin-top: var(--space-md);
  background: var(--primary-600);
  color: white;
  padding: var(--space-md) var(--space-xl);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.9375rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  transition: all var(--transition-normal);
  width: 100%;

  &:hover {
    background: var(--primary-700);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  svg {
    font-size: 1rem;
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: var(--space-4xl);
  padding: var(--space-3xl);
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--secondary-50) 100%);
  border-radius: var(--radius-xl);
`;

const CTATitle = styled.h2`
  color: var(--secondary-900);
  margin-bottom: var(--space-lg);
`;

const CTAText = styled.p`
  color: var(--secondary-600);
  margin-bottom: var(--space-xl);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(motion.a)`
  background: var(--primary-600);
  color: white;
  padding: var(--space-md) var(--space-2xl);
  border: none;
  border-radius: var(--radius-lg);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all var(--transition-normal);
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: var(--primary-700);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const ServicesPage = () => {
  const { t } = useTranslation();
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const services = [
    {
      id: 1,
      titleKey: "service1",
      icon: <FaRocket className="icon" />,
    },
    {
      id: 2,
      titleKey: "service2",
      icon: <FaGraduationCap className="icon" />,
    },
    {
      id: 3,
      titleKey: "service3",
      icon: <FaUsers className="icon" />,
    },
    {
      id: 4,
      titleKey: "service4",
      icon: <FaCogs className="icon" />,
    },
    {
      id: 5,
      titleKey: "service5",
      icon: <FaLightbulb className="icon" />,
    },
    {
      id: 6,
      titleKey: "service6",
      icon: <FaGlobe className="icon" />,
    }
  ];

  return (
    <>
      <SEO 
        title={t('services.title')}
        description={t('services.subtitle')}
        keywords="Scrum Master Services, Agile Coaching, Scrum Training, Team Transformation, Agile Consulting, Prozessoptimierung, Performance Coaching, DSGVO Compliance, DACH Markt"
      />
      <Breadcrumbs />
      <Container>
        <Header>
          <Title>{t('services.title')}</Title>
          <Subtitle>
            {t('services.subtitle')}
          </Subtitle>
        </Header>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{ scale: 1.02 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <h3>
                {service.icon}
                {t(`services.${service.titleKey}.title`)}
              </h3>
              <div className="description">{t(`services.${service.titleKey}.description`)}</div>
              <ul className="features">
                {t(`services.${service.titleKey}.features`, { returnObjects: true }).map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <div className="price-section">
                <div className="price">{t(`services.${service.titleKey}.price`)}</div>
                <div className="vat-note">{t('services.vatNote')}</div>
                <div className="duration">{t(`services.${service.titleKey}.duration`)}</div>
                <ContactButton 
                  to={`/contact?service=${encodeURIComponent(t(`services.${service.titleKey}.title`))}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToTop();
                  }}
                >
                  <FaEnvelope />
                  {t('services.contactUs')}
                </ContactButton>
              </div>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <CTASection>
          <CTATitle>{t('services.ctaTitle')}</CTATitle>
          <CTAText>
            {t('services.ctaText')}
          </CTAText>
          <CTAButton
            as={Link}
            to="/contact"
            onClick={scrollToTop}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('services.ctaButton')}
          </CTAButton>
        </CTASection>
      </Container>
    </>
  );
};

export default ServicesPage;